import { useEffect, useMemo, useState } from "react"
import Modal from "./Modal"
import { formatMoney } from "../../utils/format"
import { getApiErrorMessage } from "../../utils/errorMessage"
import { getUtilityInvoiceSettlement } from "./utilitySettlement"

const inputClass =
  "w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"

function WaiveBillModal({ providerId, invoice, onClose, onWaive, categoryOptions = [] }) {
  const hasCategoryOptions = Array.isArray(categoryOptions) && categoryOptions.length > 0
  const today = useMemo(() => new Date().toISOString().slice(0, 10), [])
  const initialCategoryValue = invoice?.utilityType || categoryOptions[0]?.value || "water"
  const [selectedTargetValue, setSelectedTargetValue] = useState(initialCategoryValue)
  const [description, setDescription] = useState("")
  const [date, setDate] = useState(today)
  const [amount, setAmount] = useState(String(getUtilityInvoiceSettlement(invoice).outstandingAmount || ""))
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")

  const selectedCategoryOption = useMemo(
    () => categoryOptions.find((option) => option.value === selectedTargetValue) || null,
    [categoryOptions, selectedTargetValue]
  )

  const targetInvoice = selectedCategoryOption
    ? {
        ...invoice,
        source: "utility_balance",
        utilityType: selectedCategoryOption.utilityType,
        outstandingAmount: selectedCategoryOption.outstandingAmount,
        description: `${selectedCategoryOption.label} utility balance`
      }
    : invoice
  const maxAmount = getUtilityInvoiceSettlement(targetInvoice).outstandingAmount

  useEffect(() => {
    setSelectedTargetValue(invoice?.utilityType || categoryOptions[0]?.value || "water")
  }, [invoice?._id, invoice?.utilityType, categoryOptions])

  const submit = async (event) => {
    event.preventDefault()
    if (submitting) return
    const normalizedAmount = Number(amount)
    if (!(normalizedAmount > 0)) {
      setError("Enter a valid waive amount.")
      return
    }
    setSubmitting(true)
    setError("")
    try {
      await onWaive(targetInvoice?.providerId || providerId, targetInvoice?._id || invoice?._id, {
        amount: normalizedAmount,
        description,
        date,
      }, targetInvoice)
      onClose()
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to waive UT bill."))
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title="Waive UT bill"
      subtitle={invoice?.customerName ? `For ${invoice.customerName}` : "Reduce remaining UT bill debt"}
      icon="receipt-cutoff"
      onClose={onClose}
      footer={
        <button
          type="submit"
          form="ut-waive-bill-form"
          disabled={submitting}
          className="w-full rounded-xl bg-amber-500 py-2.5 text-sm font-bold text-white transition-colors hover:bg-amber-600 disabled:opacity-60"
        >
          {submitting ? "Saving..." : "Create waiver"}
        </button>
      }
    >
      <form id="ut-waive-bill-form" onSubmit={submit} className="space-y-3">
        {error && (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">{error}</p>
        )}

        {hasCategoryOptions && (
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-600">Category</label>
            <select
              value={selectedTargetValue}
              onChange={(event) => setSelectedTargetValue(event.target.value)}
              className={inputClass}
            >
              {categoryOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
          <p className="font-semibold text-slate-900">{targetInvoice?.description || invoice?.description || "UT bill"}</p>
          <p className="mt-1 text-xs text-slate-500">
            Remaining balance: <span className="font-bold text-red-600">{formatMoney(maxAmount)}</span>
          </p>
        </div>

        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600">Amount to waive</label>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            className={inputClass}
            required
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600">Waive date</label>
          <input
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            className={inputClass}
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600">Reason</label>
          <input
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            className={inputClass}
            placeholder="Optional reason for the waiver"
          />
        </div>
      </form>
    </Modal>
  )
}

export default WaiveBillModal
